/**
 * API Client
 *
 * HTTP client for communicating with the backend API.
 * Includes authentication, retry logic, and error handling.
 *
 * Requirements:
 * - FR-003: Reject unauthenticated requests to protected endpoints
 * - FR-093: Include correlation ID in all requests
 * - FR-073: Surface errors explicitly - no silent failures
 */

import { getApiBaseUrl, isDevAuthBypass } from '../../../authConfig';
import { getDevAssumeUserId, getDevAssumeTenantId, getDevUserRole } from '../../../auth/devUserStore';
import { isLicenceRefusal } from '../../../auth/licenceSession';

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

/**
 * API Error
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public correlationId?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromResponse(response: Response, body: unknown, correlationId?: string): ApiError {
    const error = body as { message?: string; error?: string; details?: unknown } | null;
    const message = error?.message || error?.error || response.statusText;
    return new ApiError(
      response.status,
      response.statusText,
      message,
      correlationId,
      error?.details
    );
  }
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryStatusCodes: number[];
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Request options
 */
export interface RequestOptions extends RequestInit {
  retry?: boolean | Partial<RetryConfig>;
  timeout?: number;
  correlationId?: string;
}

/**
 * Generate a correlation ID
 */
function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Sleep utility for retry delay
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * API Client class
 */
class ApiClient {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Set the token provider function
   */
  setTokenProvider(provider: () => Promise<string | null>): void {
    this.getToken = provider;
  }

  /**
   * Get authorization headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};
    // Spec 255 (dev testing): impersonate a chosen user for the multi-user alert flow (non-prod
    // only, honored server-side). Attached alongside the real Bearer token.
    const assumeUserId = getDevAssumeUserId();
    if (assumeUserId) {
      headers['X-Dev-Assume-User-Id'] = assumeUserId;
    }

    // 🛑 THE SAME PAIR AS `services/apiClient.ts`, AND THIS IS THE SECOND OF TWO CLIENTS.
    //    This module has its OWN HTTP client, so fixing only the main one would have left every
    //    backup screen silently serving the caller's tenant while the switcher claimed
    //    otherwise — a half-fix that looks complete and is worse than none, because the working
    //    half would lend the broken half credibility.
    const assumeTenantId = getDevAssumeTenantId();
    if (assumeTenantId) {
      headers['X-Dev-Assume-Tenant-Id'] = assumeTenantId;
    }

    // Spec 285: in dev-bypass mode the backend derives the role from X-Dev-User-Role (defaulting
    // to Admin). services/apiClient.ts sends it; this second client did not, so every backup
    // screen was checked as Admin whatever role was picked — wrong both ways now that SuperAdmin
    // is enforced and the backup area is license-gated.
    if (isDevAuthBypass) {
      const devRole = getDevUserRole();
      if (devRole) headers['X-Dev-User-Role'] = devRole;
    }

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Build full URL
   */
  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    // When baseUrl is empty (production/container), use current origin as base
    const fullUrl = this.baseUrl
      ? `${this.baseUrl}${endpoint}`
      : `${window.location.origin}${endpoint}`;
    const url = new URL(fullUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Execute request with retry logic
   */
  private async executeWithRetry<T>(
    url: string,
    options: RequestOptions,
    retryConfig: RetryConfig
  ): Promise<ApiResponse<T>> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        // Add abort controller for timeout
        const controller = new AbortController();
        const timeoutId = options.timeout
          ? setTimeout(() => controller.abort(), options.timeout)
          : null;

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Parse response body
        let body: unknown;
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          body = await response.json();
        } else {
          body = await response.text();
        }

        // Check for error status
        if (!response.ok) {
          const correlationId =
            options.correlationId || response.headers.get('x-correlation-id') || undefined;
          const error = ApiError.fromResponse(response, body, correlationId);

          // Check if we should retry. Spec 286: never a licence refusal — the fetch observer has already switched the
          // SPA from this answer, and a 503 LICENSE_UNVERIFIABLE retried would ask three more times over 7 s.
          if (
            attempt < retryConfig.maxRetries &&
            retryConfig.retryStatusCodes.includes(response.status) &&
            !isLicenceRefusal({ response: { status: response.status, data: body } })
          ) {
            lastError = error;
            await sleep(retryConfig.retryDelay * Math.pow(2, attempt)); // Exponential backoff
            continue;
          }

          throw error;
        }

        return {
          data: body as T,
          status: response.status,
          headers: response.headers,
        };
      } catch (error) {
        if (error instanceof ApiError) {
          throw error;
        }

        // Handle abort/timeout
        if (error instanceof DOMException && error.name === 'AbortError') {
          lastError = new ApiError(408, 'Request Timeout', 'Request timed out');
        } else {
          lastError = error instanceof Error ? error : new Error(String(error));
        }

        // Retry on network errors
        if (attempt < retryConfig.maxRetries) {
          await sleep(retryConfig.retryDelay * Math.pow(2, attempt));
          continue;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  }

  /**
   * Make HTTP request
   */
  async request<T>(
    method: string,
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const correlationId = options.correlationId || generateCorrelationId();
    const authHeaders = await this.getAuthHeaders();

    const url = this.buildUrl(endpoint);

    const requestOptions: RequestInit = {
      ...options,
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-ID': correlationId,
        ...authHeaders,
        ...(options.headers as Record<string, string>),
      },
    };

    // Determine retry config
    let retryConfig = defaultRetryConfig;
    if (options.retry === false) {
      retryConfig = { ...retryConfig, maxRetries: 0 };
    } else if (typeof options.retry === 'object') {
      retryConfig = { ...retryConfig, ...options.retry };
    }

    return this.executeWithRetry<T>(url, { ...requestOptions, correlationId }, retryConfig);
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
    options?: RequestOptions
  ): Promise<T> {
    if (!params) {
      const response = await this.request<T>('GET', endpoint, options);
      return response.data;
    }
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, String(value));
    });
    const url = `${endpoint}?${searchParams.toString()}`;
    const response = await this.request<T>('GET', url, options);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const { body: _ignoredBody, ...restOptions } = options || {};
    const response = await this.request<T>('POST', endpoint, {
      ...restOptions,
      body: body ? JSON.stringify(body) : null,
    });
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const { body: _ignoredBody, ...restOptions } = options || {};
    const response = await this.request<T>('PUT', endpoint, {
      ...restOptions,
      body: body ? JSON.stringify(body) : null,
    });
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const { body: _ignoredBody, ...restOptions } = options || {};
    const response = await this.request<T>('PATCH', endpoint, {
      ...restOptions,
      body: body ? JSON.stringify(body) : null,
    });
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await this.request<T>('DELETE', endpoint, options);
    return response.data;
  }

  /**
   * POST request that returns the raw Response for streaming (Server-Sent Events).
   * Reuses the auth token + correlation id; the caller reads response.body.
   */
  async postStream(endpoint: string, body?: unknown, options?: RequestOptions): Promise<Response> {
    const correlationId = options?.correlationId || generateCorrelationId();
    const authHeaders = await this.getAuthHeaders();
    const url = this.buildUrl(endpoint);

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Correlation-ID': correlationId,
        ...authHeaders,
        ...(options?.headers as Record<string, string>),
      },
      body: body ? JSON.stringify(body) : null,
      credentials: 'include',
    });
  }

  /**
   * GET request that returns a Blob (for file downloads)
   * Used for downloading reports, exports, etc.
   */
  async getBlob(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
    options?: RequestOptions
  ): Promise<{ blob: Blob; filename: string }> {
    const correlationId = options?.correlationId || generateCorrelationId();
    const authHeaders = await this.getAuthHeaders();

    const url = this.buildUrl(endpoint, params);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Correlation-ID': correlationId,
        ...authHeaders,
        ...(options?.headers as Record<string, string>),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => null);
      throw ApiError.fromResponse(response, errorBody, correlationId);
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }
}

// Singleton instance
export const apiClient = new ApiClient();

// ============================================================================
// API Service Functions
// ============================================================================

/**
 * Dashboard API
 */
export const dashboardApi = {
  getSummary: (): Promise<DashboardSummary> =>
    apiClient.get<DashboardSummary>('/api/v1/dashboard/summary'),

  getBackupRuns: (params?: BackupRunsParams): Promise<PaginatedResponse<BackupRun>> =>
    apiClient.get<PaginatedResponse<BackupRun>>('/api/v1/dashboard/backup-runs', params),

  getBackupRunById: (id: string): Promise<BackupRunDetail> =>
    apiClient.get<BackupRunDetail>(`/api/v1/dashboard/backup-runs/${id}`),

  getAlerts: (params?: AlertsParams): Promise<PaginatedResponse<Alert>> =>
    apiClient.get<PaginatedResponse<Alert>>('/api/v1/dashboard/alerts', params),
};

/**
 * Auth API
 */
export const authApi = {
  login: (): Promise<UserProfile> => apiClient.post<UserProfile>('/api/v1/auth/login'),
  logout: (): Promise<{ success: boolean }> =>
    apiClient.post<{ success: boolean }>('/api/v1/auth/logout'),
  getMe: (): Promise<UserProfile> => apiClient.get<UserProfile>('/api/v1/auth/me'),
  getPermissions: (): Promise<UserPermissions> =>
    apiClient.get<UserPermissions>('/api/v1/auth/permissions'),
  refresh: (): Promise<{ success: boolean }> =>
    apiClient.post<{ success: boolean }>('/api/v1/auth/refresh'),
};

// ============================================================================
// Types
// ============================================================================

export interface DashboardSummary {
  lakehouses: {
    total: number;
    enabled: number;
    disabled: number;
    healthy: number;
    warning: number;
    error: number;
  };
  recentBackups: BackupRun[];
  alerts: {
    critical: number;
    warning: number;
    info: number;
  };
  _meta?: {
    loadTimeMs: number;
    timestamp: string;
  };
}

export interface BackupRun {
  id: string;
  lakehouse_id: string;
  lakehouse_name: string;
  workspace_name: string;
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Partial' | 'Cancelled';
  triggered_by: 'scheduled' | 'manual' | 'api';
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  environment: string;
}

export interface BackupRunDetail extends BackupRun {
  tables_backed_up?: number;
  tables_failed?: number;
  total_tables?: number;
  bytes_transferred?: number;
  remediation?: {
    category: string;
    description: string;
    steps: string[];
    documentationUrl?: string;
  };
}

export interface Alert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  lakehouse_id?: string;
  lakehouse_name?: string;
  created_at: string;
  acknowledged_at?: string;
  resolved_at?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'operator' | 'viewer';
  isActive: boolean;
  lastLogin?: string;
}

export interface UserPermissions {
  role: string;
  permissions: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  _meta?: {
    loadTimeMs: number;
    timestamp: string;
  };
}

export interface BackupRunsParams {
  [key: string]: string | number | boolean | undefined;
  page?: number;
  pageSize?: number;
  status?: string;
  lakehouseId?: string;
  workspaceId?: string;
  from?: string;
  to?: string;
  environment?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AlertsParams {
  [key: string]: string | number | boolean | undefined;
  page?: number;
  pageSize?: number;
  severity?: string;
  acknowledged?: boolean;
  resolved?: boolean;
}

export default apiClient;
